import _pick from 'lodash/pick';
import { withPulseOwner } from '../../../pulse/executionOwner';
import { isPulseOn } from '../../../pulse/floor';
import { removeDataBindingBraces } from '../../../utils/dynamicBindings';
import { evaluateResult } from '../../../utils/evaluateExpression';
import { safeGlobalContext } from '../../../utils/safeContext';

type ExecuteFunctionProps = {
  context: Record<string, unknown>;
  functionBody: string;
  shouldEvaluateResult: (result: string) => boolean;
  // the page function being evaluated; when set, pulse logs fired inside the body attribute to it
  functionId?: string;
};

// To eval a single JavaScript expression or statement and get its resolved value use indirectEvalExpression
// Permissive-by-construction: always builds on safeGlobalContext (the readWrite base)
// and takes no evalMode, so the options evaluateResult threads in are intentionally
// ignored here — a page function's mode is fixed to readWrite.
export const indirectEvalExpressionForPageFunctions = (
  script: string,
  globalContext: object,
): unknown => {
  const context = { ...safeGlobalContext, ...globalContext };
  const keys = Object.keys(context);
  const values: unknown[] = Object.values(context);
  // escape newline characters to avoid "Invalid or unexpected token" error
  // we need to preserve the newline characters in the script to maintain formatting, eg. markdown default value
  // eslint-disable-next-line @typescript-eslint/no-implied-eval, no-new-func -- needed
  const evalFunction = new Function(...keys, script);
  return evalFunction(...values);
};

export type ExecuteFunctionResult = {
  value?: unknown;
  error?: { message: string; stack: string };
};

export function executeFunction({
  context,
  functionBody,
  shouldEvaluateResult,
  functionId,
}: ExecuteFunctionProps): ExecuteFunctionResult {
  let evalError: unknown;

  const runEval = () =>
    evaluateResult({
      context,
      resolvedStringToEvaluate: removeDataBindingBraces(functionBody),
      shouldEvaluateResult,
      indirectEvalExpression: indirectEvalExpressionForPageFunctions,
      // Page functions are a deliberate script surface; a nested {{...}} result
      // re-evaluates through the default expression evaluator and must keep the
      // permissive context, not fall back to the readOnly default.
      evalMode: 'readWrite',
      onError: (e) => {
        evalError = e;
      },
    });

  // The body runs synchronously, so bracketing it with the function as the pulse owner is exact —
  // a logToDebugger fired inside attributes to this function. Gate the owner allocation on isPulseOn
  // so the runner (pulse off) allocates nothing and just runs the eval.
  const value =
    functionId && isPulseOn()
      ? withPulseOwner({ type: 'pageFunction', id: functionId }, runEval)
      : runEval();

  if (evalError) {
    return {
      error: _pick(evalError, ['message', 'stack']) as { message: string; stack: string },
    };
  }

  return { value };
}
